import { CSSProperties, ReactNode, SyntheticEvent } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SxProps,
} from '@mui/material';

import { Button, ButtonProps } from '@atom/mui';
import colors from '@atom/styles/colors';

export interface ModalProps {
  /**
   * Is the modal open/visible
   */
  open: boolean;
  /**
   * String or component displayed in modal header
   */
  title: ReactNode;
  /**
   * Options for modal size
   * @default 'sm'
   */
  width?: 'sx' | 'sm' | 'lg' | 'xl' | 'xxl' | 'xxxl' | string;
  /**
   *  If true, disables confirmButton
   */
  loading?: boolean;
  /**
   * String or component displayed in modal body
   */
  children?: ReactNode;
  /**
   * Function for closing/canceling modal
   * attached to Modal onClose prop and cancelButton onClick
   */
  onCancel?: (event?, reason?) => void;
  /**
   * Text for cancelButton
   * @default "Cancel"
   */
  cancelButtonText?: string;
  CancelButtonProps?: ButtonProps;
  /**
   * If true, clicking outside of the modal will not close it
   */
  disableCloseOnOutsideClick?: boolean;
  /**
   * If true, the modal does not move focus into itself when it opens
   */
  disableAutoFocus?: boolean;
  /**
   * confirmButton function, if not passed the button will not be rendered
   */
  onConfirm?: (event?: SyntheticEvent<any>) => void;
  /**
   * Text for confirmButton
   * @default "Ok"
   */
  confirmButtonText?: string;
  ConfirmButtonProps?: ButtonProps;
  /**
   * String or component display in modal footer, replaces default buttons
   */
  footer?: ReactNode;
  contentStyle?: CSSProperties;
  /**
   * Alias for modal TransitionProps
   */
  onExited?: () => void;
  /**
   * If true, confirmButton will be disabled
   */
  disabled?: boolean;
  DialogTitleProps?: any;
  DialogContentProps?: any;
  /**
   * If true, does not render header
   * @default false
   */
  disableHeader?: boolean;
  /**
   * If true, does not render footer
   * @default false
   */
  disableFooter?: boolean;
  /**
   * clearButton function, if not passed the button will not be rendered
   */
  onClear?: () => void;
  /**
   * Text for clearButton
   * @default "Clear"
   */
  clearButtonText?: string;
  ClearButtonProps?: ButtonProps;
  'data-cy'?: string;
}

const getWidthValue = width => {
  switch (width) {
    case 'sx':
      return '23rem';
    case 'sm':
      return '31.25rem';
    case 'lg':
      return '43.75rem';
    case 'xl':
      return '52.5rem';
    case 'xxl':
      return '62.5rem';
    case 'xxxl':
      return '77rem';
    default:
      return width;
  }
};

export const Modal = (props: ModalProps) => {
  const {
    open,
    onCancel,
    title,
    children,
    loading,
    disabled,
    footer,
    cancelButtonText = 'Cancel',
    CancelButtonProps = {},
    disableCloseOnOutsideClick = false,
    disableAutoFocus = false,
    onConfirm,
    confirmButtonText = 'Ok',
    onExited,
    ConfirmButtonProps = {},
    DialogTitleProps = {},
    DialogContentProps = {},
    disableHeader = false,
    disableFooter = false,
    onClear,
    clearButtonText = 'Clear',
    ClearButtonProps = {},
    width = 'sm',
    contentStyle = {},
    'data-cy': dataCy,
  } = props;

  const sx: SxProps = {
    minHeight: '3rem',
    ...contentStyle,
    padding: `${String(contentStyle?.padding ?? '2rem')} !important`,
  };

  const paperSx: SxProps = {
    width: getWidthValue(width),
  };

  const titleSx: SxProps = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: `1px solid ${colors.neutral.ash}`,
  };

  const footerSx: SxProps = {
    borderTop: `1px solid ${colors.neutral.ash}`,
  };

  return (
    <Dialog
      open={open}
      onClose={disableCloseOnOutsideClick ? null : onCancel}
      slotProps={{ transition: { onExited }, paper: { sx: paperSx } }}
      maxWidth={false}
      fullWidth
      disableAutoFocus={disableAutoFocus}
      data-cy={dataCy}
    >
      {!disableHeader && (
        <DialogTitle sx={titleSx} {...DialogTitleProps}>
          {title}
        </DialogTitle>
      )}
      <DialogContent sx={sx} {...DialogContentProps}>
        {children}
      </DialogContent>
      {!disableFooter && (
        <DialogActions sx={footerSx}>
          {footer || (
            <>
              {onClear && (
                <>
                  <Button onClick={onClear} {...ClearButtonProps}>
                    {clearButtonText}
                  </Button>
                  <div style={{ flexGrow: 1 }} />
                </>
              )}
              {onCancel && (
                <Button onClick={onCancel} {...CancelButtonProps}>
                  {cancelButtonText}
                </Button>
              )}
              {onConfirm && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onConfirm}
                  disabled={disabled || loading}
                  {...ConfirmButtonProps}
                >
                  {confirmButtonText}
                </Button>
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default Modal;
